import { ChangeEvent } from 'react';
import { BaseSpotComponent, SpotInputProps } from './common';
import { SpotFieldError } from './SpotFieldError';
import classNames from 'classnames';

export type SpotRadioValue = boolean | SpotInputProps['value'];

/** A change event whose `target.value` carries the original typed `value` instead of the DOM-stringified one. */
export type SpotRadioChangeEvent<TValue = SpotRadioValue> = ChangeEvent<HTMLInputElement> & { target: { value: TValue } };

export interface SpotRadioProps<TValue = SpotRadioValue> extends Omit<SpotInputProps, 'value' | 'onChange'> {
    isSmall?: boolean;
    isCentered?: boolean;
    isFullWidth?: boolean;
    value?: TValue;
    /**
     * Receives a change event whose `target.value` carries the original typed `value`
     * (e.g. `boolean`, `number`) rather than the DOM-stringified one — so consumers such
     * as react-final-form store the value with the same type it was given.
     */
    onChange?: (event: SpotRadioChangeEvent<TValue>) => void;
}

export class SpotRadio<TValue = SpotRadioValue> extends BaseSpotComponent<SpotRadioProps<TValue>> {
    // Native radio inputs serialize their `value` to a string in the DOM, so the browser's
    // change event always reports `target.value` as a string. Re-attach the original typed
    // `value` prop before forwarding, so the value keeps the type it was passed in with.
    private handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { onChange, value } = this.props;

        onChange?.({
            ...event,
            target: { ...event.target, type: event.target.type, checked: event.target.checked, value },
        } as SpotRadioChangeEvent<TValue>);
    };

    render() {
        const {
            className,
            isSmall,
            name,
            id,
            error,
            children,
            isCentered,
            isFullWidth,
            value,
            onChange,
            ['data-automation-id']: automationId,
            ...rest
        } = this.props;

        return (
            <span>
                <label
                    className={classNames(
                        'spot-form__radio',
                        {
                            'spot-form__radio--small': isSmall,
                            'spot-form__radio--full': isFullWidth,
                        },
                        className,
                    )}
                >
                    <input
                        name={name}
                        type="radio"
                        className="spot-form__radio-input"
                        value={value as string}
                        {...rest}
                        onChange={this.handleChange}
                        data-automation-id={automationId}
                    />
                    <span className="spot-form__radio-inner">
                        <span
                            className={classNames('spot-form__radio-visual', {
                                'spot-form__radio-visual--centered': isCentered,
                            })}
                        />
                        <span className="spot-form__radio-label "> {children} </span>
                    </span>
                </label>
                {error && <SpotFieldError meta={error} />}
            </span>
        );
    }
}
